import React, { useEffect, useState } from 'react';
import { chequeService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Search, Filter, RefreshCw, ChevronDown, ChevronUp, Circle, Banknote, DollarSign, XCircle, X, Plus, Edit2, Save, Trash2, MessageSquare, User, Clock, CheckCircle, AlertTriangle } from 'lucide-react';

interface Cheque {
  id: number;
  client_id: number;
  check_number: string;
  seguimiento_ck: string;
  fecha_ck: string;
  plan: string;
  cantidad: number;
  notas: string;
  facturador: string;
  status: string;
  company_name: string;
  client_name: string;
  created_at: string;
}

const statusConfig: Record<string, { bg: string; text: string; label: string; border: string; dot: string }> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pendiente', border: 'border-amber-300', dot: '#F59E0B' },
  in_process: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En Proceso', border: 'border-blue-300', dot: '#3B82F6' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completado', border: 'border-emerald-300', dot: '#10B981' },
  denied: { bg: 'bg-red-100', text: 'text-red-700', label: 'Denegado', border: 'border-red-300', dot: '#EF4444' },
  paid: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Pagado', border: 'border-violet-300', dot: '#8B5CF6' },
};

const ChequeList: React.FC = () => {
  const { user } = useAuth();
  const [cheques, setCheques] = useState<Cheque[]>([]);
  const [filteredCheques, setFilteredCheques] = useState<Cheque[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<'cheques' | 'paid' | 'denied' | 'completed'>('cheques');

  // Detail modal
  const [selectedCheque, setSelectedCheque] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Edit modal
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCheques = async () => {
    setIsLoading(true);
    try {
      const response = await chequeService.getAll();
      setCheques(response.data);
      setFilteredCheques(response.data);
    } catch (error) {
      console.error('Error fetching cheques:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCheques();
  }, []);

  useEffect(() => {
    let result = cheques;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(ck =>
        ck.check_number?.toLowerCase().includes(term) ||
        ck.seguimiento_ck?.toLowerCase().includes(term) ||
        ck.plan?.toLowerCase().includes(term) ||
        ck.notas?.toLowerCase().includes(term) ||
        ck.company_name?.toLowerCase().includes(term) ||
        ck.facturador?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(ck => ck.status === statusFilter);
    }

    setFilteredCheques(result);
  }, [searchTerm, statusFilter, cheques]);

  const openDetail = async (cheque: Cheque) => {
    try {
      const res = await chequeService.getById(cheque.id);
      setSelectedCheque({ ...res.data, company_name: cheque.company_name, client_name: cheque.client_name });
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching cheque detail:', error);
    }
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedCheque(null);
    setIsEditing(false);
    setEditData({});
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selectedCheque) return;
    try {
      await chequeService.update(selectedCheque.id, { status: newStatus });
      const updated = { ...selectedCheque, status: newStatus };
      setSelectedCheque(updated);
      setCheques(prev => prev.map(ck => ck.id === selectedCheque.id ? { ...ck, status: newStatus } : ck));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const startEditing = () => {
    if (selectedCheque) {
      setEditData({
        check_number: selectedCheque.check_number,
        seguimiento_ck: selectedCheque.seguimiento_ck,
        plan: selectedCheque.plan,
        fecha_ck: selectedCheque.fecha_ck ? selectedCheque.fecha_ck.split('T')[0] : '',
        notas: selectedCheque.notas,
        facturador: selectedCheque.facturador,
        status: selectedCheque.status,
      });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedCheque) return;
    setIsSaving(true);
    try {
      await chequeService.update(selectedCheque.id, editData);
      const updated = { ...selectedCheque, ...editData };
      setSelectedCheque(updated);
      setCheques(prev => prev.map(ck => ck.id === selectedCheque.id ? { ...ck, ...editData } : ck));
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving cheque:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCheque) return;
    if (!window.confirm(`¿Eliminar cheque #${selectedCheque.check_number}?`)) return;
    setIsDeleting(true);
    try {
      await chequeService.delete(selectedCheque.id);
      setCheques(prev => prev.filter(ck => ck.id !== selectedCheque.id));
      closeDetail();
    } catch (error) {
      console.error('Error deleting cheque:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getTabCheques = (tab: string) => {
    if (tab === 'cheques') return filteredCheques.filter(ck => !['paid', 'denied', 'completed'].includes(ck.status));
    if (tab === 'completed') return filteredCheques.filter(ck => ck.status === 'completed');
    if (tab === 'paid') return filteredCheques.filter(ck => ck.status === 'paid');
    if (tab === 'denied') return filteredCheques.filter(ck => ck.status === 'denied');
    return filteredCheques;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Listado de Cheques</h1>
          <p className="text-gray-500">Todos los cheques del sistema</p>
        </div>
        <button
          onClick={fetchCheques}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
        >
          <RefreshCw size={18} />
          Actualizar
        </button>
      </div>

      <div className="bg-white rounded-xl shadow">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('cheques')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'cheques'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Banknote size={16} />
              Cheques ({cheques.filter(ck => !['paid', 'denied', 'completed'].includes(ck.status)).length})
            </button>
            <button
              onClick={() => setActiveTab('paid')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'paid'
                  ? 'border-violet-600 text-violet-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign size={16} />
              Pagados ({cheques.filter(ck => ck.status === 'paid').length})
            </button>
            <button
              onClick={() => setActiveTab('denied')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'denied'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <XCircle size={16} />
              Denegados ({cheques.filter(ck => ck.status === 'denied').length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'completed'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CheckCircle size={16} />
              Completados ({cheques.filter(ck => ck.status === 'completed').length})
            </button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por número, plan, empresa, notas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="in_process">En Proceso</option>
              <option value="completed">Completado</option>
              <option value="denied">Denegado</option>
              <option value="paid">Pagado</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cheque</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha CK</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facturador</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notas</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {getTabCheques(activeTab).map((cheque) => {
                const st = statusConfig[cheque.status] || statusConfig.pending;
                return (
                  <tr key={cheque.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Circle size={10} fill={st.dot} stroke="none" />
                        <Link
                          to={`/clients?selected=${cheque.client_id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {cheque.company_name || cheque.client_name || '-'}
                        </Link>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <button
                        onClick={() => openDetail(cheque)}
                        className="font-medium text-emerald-600 hover:text-emerald-800 hover:underline"
                      >
                        #{cheque.check_number || '-'}
                      </button>
                      {cheque.seguimiento_ck && (
                        <span className="text-gray-400 ml-2 text-xs">{cheque.seguimiento_ck}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium border ${st.bg} ${st.text} ${st.border}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {cheque.plan || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {cheque.fecha_ck ? new Date(cheque.fecha_ck).toLocaleDateString('en-US') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                        {cheque.facturador || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                      <div className="line-clamp-2 text-xs">{cheque.notas || '-'}</div>
                    </td>
                  </tr>
                );
              })}
              {getTabCheques(activeTab).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No se encontraron cheques
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 text-sm text-gray-500">
          Mostrando {getTabCheques(activeTab).length} de {cheques.length} cheques
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCheque && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Cheque #{selectedCheque.check_number || '-'}
                </h2>
                <p className="text-sm text-gray-500">
                  {selectedCheque.company_name || selectedCheque.client_name || '-'} • {selectedCheque.plan || '-'}
                </p>
              </div>
              <button onClick={closeDetail} className="p-2 hover:bg-gray-200 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="flex">
                {/* Left Panel - Notes & Facturas */}
                <div className="flex-1 p-4 border-r border-gray-200">
                  {/* Notas */}
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <MessageSquare size={16} />
                      Notas
                    </h3>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
                      {selectedCheque.notas || 'Sin notas'}
                    </div>
                  </div>

                  {/* Facturas asociadas */}
                  {selectedCheque.facturas && selectedCheque.facturas.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Facturas Asociadas</h3>
                      <div className="space-y-2">
                        {selectedCheque.facturas.map((f: any) => (
                          <div key={f.id} className="flex items-center justify-between bg-blue-50 p-2 rounded-lg text-sm">
                            <div>
                              <span className="font-mono font-medium text-blue-700">#{f.factura_numero}</span>
                              {f.notas && <span className="text-gray-500 ml-2">{f.notas}</span>}
                            </div>
                            <span className="text-xs text-gray-400">{f.facturador || '-'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Logs */}
                  {selectedCheque.logs && selectedCheque.logs.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Historial</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedCheque.logs.map((log: any) => (
                          <div key={log.id} className="flex gap-2 text-xs">
                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <User size={12} className="text-gray-500" />
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">{log.first_name} {log.last_name}</span>
                              <span className="text-gray-400 ml-2">{new Date(log.created_at).toLocaleDateString('en-US')}</span>
                              <p className="text-gray-600 mt-0.5">{log.nota}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Panel - Properties */}
                <div className="w-72 p-4 bg-gray-50">
                  {/* Status */}
                  <div className="mb-4">
                    {(() => {
                      const st = statusConfig[selectedCheque.status] || statusConfig.pending;
                      return (
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${st.bg} ${st.text}`}>
                          {st.label}
                        </span>
                      );
                    })()}
                  </div>

                  {/* Properties */}
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="text-xs text-gray-500">Seguimiento</label>
                      <p className="text-sm font-medium">{selectedCheque.seguimiento_ck || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Plan</label>
                      <p className="text-sm font-medium">{selectedCheque.plan || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Fecha CK</label>
                      <p className="text-sm">{selectedCheque.fecha_ck ? new Date(selectedCheque.fecha_ck).toLocaleDateString('en-US') : '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Facturador</label>
                      <p className="text-sm font-medium">{selectedCheque.facturador || '-'}</p>
                    </div>
                    {user?.role === 'admin' && (
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Cambiar Estado</label>
                        <select
                          value={selectedCheque.status}
                          onChange={(e) => handleStatusChange(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="pending">Pendiente</option>
                          <option value="in_process">En Proceso</option>
                          <option value="completed">Completado</option>
                          <option value="denied">Denegado</option>
                          <option value="paid">Pagado</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {user?.role === 'admin' && (
                    <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                      <button
                        onClick={startEditing}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm"
                      >
                        <Edit2 size={14} />
                        Editar
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded text-sm disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                        Eliminar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditing && selectedCheque && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Editar Cheque</h2>
              <button onClick={() => setIsEditing(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"># Cheque</label>
                  <input
                    type="text"
                    value={editData.check_number || ''}
                    onChange={(e) => setEditData({ ...editData, check_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Seguimiento</label>
                  <input
                    type="text"
                    value={editData.seguimiento_ck || ''}
                    onChange={(e) => setEditData({ ...editData, seguimiento_ck: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <input
                    type="text"
                    value={editData.plan || ''}
                    onChange={(e) => setEditData({ ...editData, plan: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha CK</label>
                  <input
                    type="date"
                    value={editData.fecha_ck || ''}
                    onChange={(e) => setEditData({ ...editData, fecha_ck: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Facturador</label>
                  <input
                    type="text"
                    value={editData.facturador || ''}
                    onChange={(e) => setEditData({ ...editData, facturador: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={editData.status || ''}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_process">En Proceso</option>
                    <option value="completed">Completado</option>
                    <option value="denied">Denegado</option>
                    <option value="paid">Pagado</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={editData.notas || ''}
                  onChange={(e) => setEditData({ ...editData, notas: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChequeList;
